import { useState, useRef, ChangeEvent, useCallback } from 'react'
import {
  Flex,
  Box,
  BoxProps,
  Checkbox,
  Label,
  LabelProps,
  InputProps,
} from 'theme-ui'
import { FilterButton, Paper, Text, Button } from 'components'
import { useClickOutside, useHandleEscapeKey } from 'hooks'
import { StatusType, TMATTStatusType } from 'types'
import { STATUS_LABELS, TMATT_STATUS_LABELS } from '../../constants'
import { noop } from 'utils'

export type FilterProps = BoxProps & {
  open?: boolean
  filters?: StatusType[]
  progressFilters?: StatusType[]
  typeFilters?: StatusType[]
  disabled?: boolean
  handleSubmit?: (filters: StatusType[], progress: StatusType[]) => void
}

export type FilterItemProps = LabelProps

const LabelItem = ({ children, ...rest }: FilterItemProps) => {
  const { sx, ...props } = rest
  return (
    <Label
      sx={{
        px: 5,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        ...rest.sx,
      }}
      variant="forms.filter"
      {...props}>
      {children}
    </Label>
  )
}

const InputCheckbox = ({ ...rest }: InputProps) => {
  const { sx, ...props } = rest
  return (
    <Checkbox
      sx={{
        marginRight: 2,
      }}
      {...props}
    />
  )
}

type State = StatusType[]

const TMATTall: Partial<TMATTStatusType>[] = [
  'waiting',
  'pre-course',
  'signature-pending',
  'completed',
  'progress',
  'expired',
  'deactivated',
]

const progress: Partial<StatusType>[] = ['done', 'not-done']

export function TMATTFilter({
  open = false,
  filters = TMATTall,
  progressFilters = progress,
  disabled = false,
  handleSubmit = noop,
}: FilterProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState<boolean>(open)
  const [selected, set] = useState<State>(filters)
  const [selectedProgress, setSelectedProgress] = useState<State>(
    progressFilters
  )
  const hide = useCallback(() => {
    show && setShow(false)
  }, [setShow, show])
  useClickOutside(ref, hide)
  useHandleEscapeKey(hide)

  function handleAllClick() {
    set(filters)
    setSelectedProgress(progress)
  }

  function handleChange(e: ChangeEvent<any>) {
    const { value } = e.target

    if (!selected.includes(value)) {
      set([...selected, value])
    } else {
      const filtered = selected.filter((status) => status !== value)
      set(filtered)
    }
  }

  function handleProgressFilterChange(e: ChangeEvent<any>) {
    const { value } = e.target
    if (!selectedProgress.includes(value)) {
      setSelectedProgress([...selectedProgress, value])
    } else {
      const filtered = selectedProgress.filter((status) => status !== value)
      setSelectedProgress(filtered)
    }
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: 'inline-block',
        position: 'relative',
      }}>
      <FilterButton
        disabled={disabled}
        onClick={() => {
          if (!disabled) setShow(!show)
        }}
      />
      <Paper
        sx={{
          p: 0,
          mt: 2,
          position: 'absolute',
          width: 320,
          right: -1,
          zIndex: 9999,
          transition: '400ms ease-in-out',
          transform: show ? 'scale(1)' : 'scale(0.95)',
          transformOrigin: 'right top',
          opacity: show ? 1 : 0,
          visibility: show ? 'visible' : 'hidden',
          pointerEvents: show ? 'auto' : 'none',
        }}
        withShadow>
        <Flex sx={{ flexDirection: 'row' }}>
          <div>
            <LabelItem
              htmlFor="all"
              sx={{
                py: 3,
              }}>
              <Text sx={{ fontWeight: 'bold', color: '#26344A' }}>
                All statuses
              </Text>
            </LabelItem>
            {TMATTall.map((field, index) => (
              <LabelItem htmlFor={field} key={`filter-${index}`}>
                <InputCheckbox
                  id={field}
                  name="filter"
                  value={field}
                  checked={selected?.includes(field)}
                  onChange={handleChange}
                />
                <Text>{TMATT_STATUS_LABELS[field]}</Text>
              </LabelItem>
            ))}
          </div>
          <div>
            <LabelItem
              htmlFor="all"
              sx={{
                py: 3,
              }}>
              <Text sx={{ fontWeight: 'bold' }}>Position</Text>
            </LabelItem>
            {progress.map((field, index) => (
              <LabelItem htmlFor={field} key={`filter-${index}`}>
                <InputCheckbox
                  id={field}
                  name="filter"
                  value={field}
                  checked={selectedProgress?.includes(field)}
                  onChange={handleProgressFilterChange}
                />
                <Text>{STATUS_LABELS[field]}</Text>
              </LabelItem>
            ))}
          </div>
        </Flex>

        <Flex sx={{ pt: 2, pb: 4, px: 3 }}>
          <Button
            sx={{ flex: 1 }}
            px={1}
            disabled={selected.length === 0 || selectedProgress.length === 0}
            data-cy="filter-results"
            onClick={() => {
              setShow(false)
              handleSubmit(selected, selectedProgress)
            }}>
            Confirm
          </Button>
          <Button
            sx={{ flex: 1 }}
            style={{
              outline: 'none',
              border: 'none',
              boxShadow: 'none',
            }}
            variant="outline"
            onClick={handleAllClick}>
            Show All
          </Button>
        </Flex>
      </Paper>
    </Box>
  )
}
